import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import { drugSearchActions, getMemberAction, getPharmacyActions } from "@/App/actions/";
import ResponseSegmentContainer from "@/App/components/ResponseComponents/ResponseSegmentContainer";
import TestClaimLayout from "@/App/layouts/TestClaim";
import { RootState } from "@/App/reducers";
import { formatTestClaimResponse } from "@/App/utils/testClaimUtils";
import { getDrugsFromQueryString } from "@/App/utils/utils";
import { checkDate, queryString } from "capitalrx-utilities";

import { TestClaimFormattedResponseData } from "@/types/appTypes/transmissions";

import TestClaimForm from "./TestClaimForm";

const TestClaim: React.FC = () => {
  const { search } = useLocation();
  const {
    date_of_service,
    prescription_service_reference_number,
    days_supply,
    fill_number,
    daw,
    usual_and_customary_charge,
    submission_clarification_code_1,
    diagnosis_code_qualifier_1,
    diagnosis_code_1,
    patient_residence,
    place_of_service,
    pharmacy_service_type,
    prescriber_id,
    prescriber_id_qualifier,
    result_of_service_code,
    reason_for_service_code,
    dur_pps_level_of_effort,
    professional_service_code,
    prescription_origin_code,
    drugs,
    member_id,
    npi,

    ingredient_cost_submitted,
    dispensing_fee_submitted,
    incentive_amount_submitted,

    other_coverage_code,
    other_payer_amount_paid_1,
    other_payer_patient_responsibility_amount_1,
    other_payer_reject_code_1,
    other_payer_id_1,
    other_payer_id_qualifier_1,

    special_packaging_indicator,
  } = queryString.getQueryParams(search);

  const dispatch = useDispatch();
  const { getMember } = getMemberAction;
  const { getPharmacy } = getPharmacyActions;
  const { searchForSelectedDrugs } = drugSearchActions;

  const { memberInformation } = useSelector((state: RootState) => state.memberInformation);
  const { plan } = useSelector((state: RootState) => state.planGet);
  const { group } = useSelector((state: RootState) => state.groupGet);
  const { planAccount } = useSelector((state: RootState) => state.planAccount);

  const { pharmacy } = useSelector((state: RootState) => state.pharmacyGet);
  const responseData = useSelector((state: RootState) => state.transmissionTest.result);

  const { selectedDrugs } = useSelector((state: RootState) => state.drug);

  const currentDrugs = getDrugsFromQueryString(drugs);

  const initializeForm = (): object => {
    const filteredDrugs = currentDrugs.filter((drug) => drug.ndc);
    const finalDrugs = filteredDrugs.map((drug) => {
      for (const selectedDrug of selectedDrugs) {
        if (selectedDrug.ndc === drug.ndc) {
          return {
            ...selectedDrug,
            ...drug,
          };
        }
      }
    });

    return {
      plan: {
        ...plan.details,
      },
      group: {
        ...group.details,
      },
      plan_account: {
        ...planAccount,
      },
      ...memberInformation,
      details: {
        ...memberInformation.details,
        birth_date: checkDate(memberInformation.details.birth_date),
      },
      pharmacy: { ...pharmacy },
      drugs: finalDrugs,

      date_of_service,
      prescription_service_reference_number,
      days_supply,
      fill_number,
      daw,
      usual_and_customary_charge,
      submission_clarification_code_1,
      diagnosis_code_qualifier_1,
      diagnosis_code_1,
      patient_residence,
      place_of_service,
      pharmacy_service_type,
      prescriber_id,
      prescriber_id_qualifier,
      result_of_service_code,
      reason_for_service_code,
      dur_pps_level_of_effort,
      professional_service_code,
      prescription_origin_code,

      ingredient_cost_submitted,
      dispensing_fee_submitted,
      incentive_amount_submitted,

      other_coverage_code,
      other_payer_amount_paid_1,
      other_payer_patient_responsibility_amount_1,
      other_payer_reject_code_1,
      other_payer_id_1,
      other_payer_id_qualifier_1,

      special_packaging_indicator,
    };
  };

  useEffect(() => {
    if (member_id) {
      dispatch(getMember(member_id as string));
    }

    if (npi) {
      dispatch(getPharmacy(npi as string));
    }

    if (currentDrugs) {
      dispatch(
        searchForSelectedDrugs({
          data: currentDrugs,
          formName: "testClaim",
        }),
      );
    }
  }, []);

  // format this data
  const rawResponseData = useSelector((state: RootState) => state.transmissionTest.result);

  const formattedResponseData: TestClaimFormattedResponseData = formatTestClaimResponse(rawResponseData);

  let responseSegment = null;

  if (responseData.transaction_response_status) {
    responseSegment = <ResponseSegmentContainer responseData={formattedResponseData} />;
  }

  return (
    <TestClaimLayout>
      <TestClaimForm initialValues={initializeForm()} />
      {responseSegment}
    </TestClaimLayout>
  );
};

export default TestClaim;
